/* eslint-disable no-nested-ternary */
import { Header1, LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import { FormValue } from '@flatsy/types/Applications'
import { Button, Grid } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { FormContext, ReturnContextProps } from 'src/containers/Qualification/Context'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import FlatsyButtonsField from '@flatsy/core/Form/ReactFormHooks/Field/Buttons'
import FlatsyDateField from '@flatsy/core/Form/ReactFormHooks/Field/Date'
import FlatsyTextField from '@flatsy/core/Form/ReactFormHooks/Field/Text'
import i18n from '@flatsy/utils/translations'
import { useFormStyles } from '../../Style'
import { SharedProps } from '../../Type'
import useStyles from '../Style'
import { ProjectProData } from './Type'
import { projectCompanyCreation } from './Utils'

const ProjectPro: React.FC<SharedProps> = ({ withOutApplication, renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const classes = useStyles()
  const {
    wizardContext: { context },
    saveApplicationInRedux,
    saveApplicationAndContinue,
  } = useContext(FormContext) as ReturnContextProps
  const { application } = context

  const errorTxtCompanyTurnover = `${i18n.t('company_turnover')} ${i18n
    .t('is_a_required_field')
    .toLowerCase()}`

  const schema = yup.object({
    company_is_created: yup.string().required(),
    company_turnover: yup.string().required(errorTxtCompanyTurnover),
    company_creation_date: yup
      .string()
      .nullable()
      .when('company_is_created', {
        is: (val: string) => val !== FormValue.companyCreation.COMPANY_IN_PROCESS,
        then: yup.string().required().typeError(TextError.empty),
      }),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<ProjectProData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  const company_is_created = watch('company_is_created')

  useEffect(() => {
    if (application) {
      reset({
        company_is_created:
          application.company_is_created === true
            ? 'true'
            : application.company_is_created === false
            ? 'false'
            : undefined,
        company_turnover: application.company_turnover,
        company_creation_date: application.company_creation_date,
      })
    }
  }, [application])

  useEffect(() => {
    if (company_is_created) {
      setValue('company_creation_date', undefined)
    }
  }, [company_is_created])

  if (!application) {
    return null
  }

  const onSubmit = async (values: ProjectProData): Promise<undefined> => {
    if (withOutApplication) {
      saveApplicationInRedux({
        ...values,
        company_is_created: values.company_is_created === 'true',
      })
    } else {
      saveApplicationAndContinue({
        ...values,
        company_is_created: values.company_is_created === 'true',
      })
    }
    return undefined
  }

  return (
    <div className={formClasses.container}>
      <div className={classes.intro}>
        <Header1 className={classes.introTitle} lowerCase align="center">
          {i18n.t('welcome')}
        </Header1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('is_your_business_already_established')}
        </LabelSeparator>
        <Controller
          name="company_is_created"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={projectCompanyCreation()}
            />
          )}
        />
        {company_is_created !== FormValue.companyCreation.COMPANY_IN_PROCESS && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {i18n.t('when_was_your_business_founded')}
            </LabelSeparator>
            <Grid sm={6} xs={12}>
              <Controller
                name="company_creation_date"
                control={control}
                render={({ field, fieldState }) => (
                  <FlatsyDateField
                    {...field}
                    {...fieldState}
                    isSubmitSuccessful={isSubmitSuccessful}
                  />
                )}
              />
            </Grid>
          </>
        )}
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('what_is_your_turnover_or_projected_turnover')}
        </LabelSeparator>
        <Grid sm={6} xs={12}>
          <Controller
            name="company_turnover"
            control={control}
            render={({ field, fieldState }) => (
              <FlatsyTextField
                {...field}
                {...fieldState}
                isSubmitSuccessful={isSubmitSuccessful}
                type="number"
                inputAdornment={i18n.t('@€')}
              />
            )}
          />
        </Grid>
        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isDirty, !isValid)
        )}
      </form>
    </div>
  )
}

export default ProjectPro
